import { makeStyles } from "tss-react/mui"
import { Card, Typography, Box, Divider, IconButton, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faUserGroup,
    faPlateUtensils,
    faLocation,
    faClock,
    faLocationDot,
    faTruck,
} from "@fortawesome/pro-light-svg-icons"
import { faEllipsisV, faCalendarCheck, faLock, faMugSaucer } from "@fortawesome/pro-solid-svg-icons"
import { MenuMeeting, ReadonlyOrder } from "../../../weekMenuTypes"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { MeetingProgressBar } from "./MeetingProgressBar"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { SectionWithIcon } from "../../SectionWithIcon"
import { useMemo, useState } from "react"
import { MeetingCardMenu } from "./MeetingCardMenu"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import {
    DeliveryType,
    getDeliveryTypeValue,
} from "../../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { SimpleStatusChip } from "../../../../../../merchant/merchant/production/meetingService/components/StatusMenu"

const useStyles = makeStyles()(() => ({
    card: {
        position: "relative",
        borderRadius: ".5rem",
        backgroundColor: "#fff",
        padding: "1rem",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
        width: "100%",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: ".5rem",
    },
    divider: {
        margin: ".75rem 0",
    },
    iconWrapper: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: ".5rem",
        padding: ".7rem",
        width: "2.5rem",
        height: "2.5rem",
    },
    meetingDetailsIcon: {
        color: tailwindPalette.purple[900],
        backgroundColor: tailwindPalette.purple[200],
    },
    meetingServiceIcon: {
        color: tailwindPalette.teal[900],
        backgroundColor: tailwindPalette.teal[200],
    },
    iconXs: {
        fontSize: ".5rem",
        color: "#94A3B8",
    },
    chipContainer: {
        marginBottom: "1rem",
    },
    moreMenu: {
        position: "absolute",
        top: "1rem",
        right: "1rem",
    },
}))

const mapMeeting = (meeting: ReadonlyOrder): MenuMeeting => {
    const { meetingName, endDateTime, startDateTime } = meeting.meetingDetails

    return {
        id: meeting.id,
        uid: meeting.uid,
        orderSummary: meeting.orderSummary,
        deliveryDetails: {
            time: meeting.deliveryDetails.time || startDateTime, // used in receipt header select
        },
        organizers: meeting.organizers,
        meetingDetails: {
            meetingName,
            startDateTime: meeting.deliveryDetails.time,
            endDateTime,
        },
    }
}

export function MeetingCard({ meeting, meetings }: { meeting: ReadonlyOrder; meetings: ReadonlyOrder[] }) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const menuMeeting = useMemo((): MenuMeeting => mapMeeting(meeting), [meeting])
    const allMeetingsFormatted = useMemo((): MenuMeeting[] => meetings.map(mapMeeting), [meetings])

    const deliveryTypeMessage = useMemo(() => {
        const deliveryTypeValue = getDeliveryTypeValue(meeting.deliveryDetails.type)

        if (deliveryTypeValue === DeliveryType.DELIVERY) {
            return `${deliveryTypeValue}: ${meeting.deliveryDetails.location.name}`
        }
        return deliveryTypeValue
    }, [meeting.deliveryDetails.type])

    return (
        <Card className={classes.card}>
            <MeetingProgressBar start={meeting.meetingDetails.startDateTime} end={meeting.meetingDetails.endDateTime} />

            {meeting.orderSummary.hasBooking && (
                <Stack direction="row" spacing="1rem" alignItems="start">
                    <Box className={cx(classes.iconWrapper, classes.meetingDetailsIcon)}>
                        <FontAwesomeIcon icon={faCalendarCheck} color="a855f7" />
                    </Box>
                    <Box width="100%">
                        <Box className={classes.header}>
                            <Typography variant="subtitle1">{meeting.meetingDetails.meetingName}</Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary">
                            {meeting.meetingDetails.meetingTimeAsText}
                        </Typography>

                        <Divider className={classes.divider} />
                        <SectionWithIcon icon={faLocationDot}>
                            <Typography variant="body2" color="textSecondary">
                                {meeting.meetingDetails.location}
                            </Typography>
                        </SectionWithIcon>
                        {!!meeting?.orderSummary?.participants && (
                            <SectionWithIcon icon={faUserGroup}>
                                <LocalizedStrict
                                    id="user-home-upcoming-meeting-service-attendees"
                                    vars={{ attendees: meeting?.orderSummary?.participants }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {meeting?.orderSummary?.participants} participants
                                    </Typography>
                                </LocalizedStrict>
                            </SectionWithIcon>
                        )}
                    </Box>
                </Stack>
            )}

            {meeting.orderSummary.hasCatering && (
                <>
                    {meeting.orderSummary.hasBooking && <Divider className={classes.divider} />}
                    <Stack direction="row" spacing="1rem" alignItems="start">
                        <Box className={cx(classes.iconWrapper, classes.meetingServiceIcon)}>
                            <FontAwesomeIcon icon={faMugSaucer} color="#14b8a6" />
                        </Box>
                        <Box width="100%">
                            <Box className={classes.header}>
                                <Typography variant="subtitle1">{meeting.meetingDetails.meetingName}</Typography>
                            </Box>
                            <SimpleStatusChip
                                className={classes.chipContainer}
                                status={meeting.deliveryDetails?.statusDetails.status}
                                tooltipLabel={meeting.deliveryDetails?.statusDetails.message}
                                size="small"
                            />
                            <Stack direction="column" spacing=".5rem">
                                {meeting.deliveryDetails.isLocked && (
                                    <Stack direction="row" spacing=".5rem" justifyContent="start" alignContent="center">
                                        <LocalizedStrict id="user-home-upcoming-meeting-service-locked">
                                            <Typography variant="body2" color="textSecondary">
                                                Locked
                                            </Typography>
                                        </LocalizedStrict>
                                        <Stack justifyContent="center" alignContent="center">
                                            <FontAwesomeIcon icon={faLock} className={classes.iconXs} />
                                        </Stack>
                                    </Stack>
                                )}
                                <SectionWithIcon icon={faClock}>
                                    <Typography variant="body2" color="textSecondary">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(meeting.deliveryDetails.time),
                                        )}
                                    </Typography>
                                </SectionWithIcon>
                                {meeting.deliveryDetails.sittings && (
                                    <SectionWithIcon icon={faPlateUtensils}>
                                        <LocalizedStrict
                                            id="user-home-upcoming-meeting-service-sittings"
                                            vars={{ sittings: meeting.deliveryDetails.sittings }}
                                        >
                                            <Typography variant="body2" color="textSecondary">
                                                {meeting.deliveryDetails.sittings} sittings
                                            </Typography>
                                        </LocalizedStrict>
                                    </SectionWithIcon>
                                )}
                                <SectionWithIcon icon={faTruck}>
                                    <Typography variant="body2" color="textSecondary" lineHeight={1.7}>
                                        {deliveryTypeMessage}
                                    </Typography>
                                </SectionWithIcon>
                            </Stack>
                        </Box>
                    </Stack>
                </>
            )}

            <Box className={classes.moreMenu}>
                <Box className={classes.header}>
                    <IconButton size="small" onClick={handleMenuOpen}>
                        <FontAwesomeIcon icon={faEllipsisV} />
                    </IconButton>
                </Box>

                {!!anchorEl && (
                    <MeetingCardMenu
                        meeting={menuMeeting}
                        anchorEl={anchorEl}
                        meetings={allMeetingsFormatted}
                        menuOnApi
                        handleMenuClose={handleMenuClose}
                    />
                )}
            </Box>
        </Card>
    )
}
